import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Tooltip } from '@mui/material';
import { useSnackbar } from 'src/components/snackbar';
import { useEditMetric } from 'src/data/impact/mutation/impact-mutation';
import { useGetUser } from 'src/hooks/useGetUser';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const VDebut = ({ id, data }: { id: string; data: string }) => {
  const user = useGetUser();
  const [isEditing, setIsEditing] = useState(false);
  const [startv, setStartv] = useState<string>(data || '...');
  const quillRef = useRef<ReactQuill>(null);
  const [showToolbar, setShowToolbar] = useState(false);
  const [isFirstClick, setIsFirstClick] = useState(true);
  const toolbarRef = useRef<HTMLDivElement>(null);

  const editMetric = useEditMetric();
  const { enqueueSnackbar } = useSnackbar();

  const colorPalette = [
    '#000000', // Black
    '#FFFFFF', // White
    '#FF0000', // Red
    '#00AA55', // Green
    '#0099F2', // Blue
    '#FFFF00', // Yellow
    '#FF00FF', // Magenta
    '#00FFFF', // Cyan
  ];

  const icons = Quill.import('ui/icons');

  // Dynamically create icons for each color
  colorPalette.forEach((color) => {
    icons[color] = `<svg viewBox="0 0 18 18" width="18" height="18">
    <circle cx="9" cy="9" r="7" fill="${color}" stroke="black" stroke-width="1"/>
  </svg>`;
  });

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [['bold', 'italic', ...colorPalette]],
        handlers: {
          color: function (this: any, value: string) {
            const quill = this.quill;
            const range = quill.getSelection();

            if (range) {
              quill.format('color', value);
              setShowToolbar(true);
            }
          },
          ...colorPalette.reduce((handlers, color) => {
            handlers[color] = function (this: any) {
              const quill = this.quill;
              const range = quill.getSelection();

              if (range) {
                quill.format('color', color);
                setShowToolbar(true);
              }
            };
            return handlers;
          }, {} as Record<string, (this: any) => void>),
        },
      },
    }),
    []
  );
  const formats = ['bold', 'italic', 'color'];

  // Sync local state with prop changes
  useEffect(() => {
    setStartv(data || '...');
  }, [data]);

  const handleUpdateDebut = () => {
    editMetric.mutate(
      {
        startv: startv.toString(),
        id,
      },
      {
        onSuccess: (res) => {
          enqueueSnackbar('mis à jour avec succès.');
        },
        onError: (err) => {
          enqueueSnackbar("quelque chose s'est mal passé", {
            variant: 'error',
          });
        },
      }
    );
  };
  // Function to strip HTML tags
  const stripHtmlTags = (html: string) => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const handleEditClick = () => {
    if (user && user.role === 'admin') {
      if (isFirstClick) {
        setIsFirstClick(false);
        setIsEditing(true);
      } else {
        setIsEditing(true);
      }
    }
  };

  const handleBlur = () => {
    setIsEditing(false);
    setShowToolbar(false);

    editMetric.mutate(
      {
        startv: startv.toString(),
        id,
      },
      {
        onSuccess: (res) => {
          enqueueSnackbar('mis à jour avec succès.');
        },
        onError: (err) => {
          enqueueSnackbar("quelque chose s'est mal passé", {
            variant: 'error',
          });
        },
      }
    );
  };
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (quillRef.current && toolbarRef.current) {
        const quillContainer = quillRef.current.getEditor().root;
        const targetNode = event.target as Node;

        // Find the closest parent container that includes both quill and toolbar
        const editorContainer = quillContainer.closest('.relative');

        // Check if the click is completely outside the editor container
        if (editorContainer && !editorContainer.contains(targetNode)) {
          if (!isFirstClick) {
            setShowToolbar(false);
            setIsEditing(false);
            handleUpdateDebut();
          }
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFirstClick, handleUpdateDebut]);

  useEffect(() => {
    if (isEditing) {
      setShowToolbar(true);
    }
  }, [isEditing]);

  return (
    <Tooltip title={stripHtmlTags(startv)}>
      <div className="flex-grow text-thin text-xs border-r-2 ">
        {isEditing && user && user.role === 'admin' ? (
          <div className="relative">
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={startv}
              onChange={setStartv}
              onBlur={() => {
                handleUpdateDebut();
                setIsEditing(false);
                setShowToolbar(false);
              }}
              modules={modules}
              formats={formats}
              className="min-w-[60px] w-full text-center custom-quill-editor relative"
            />
            {showToolbar && <div ref={toolbarRef} className="custom-toolbar"></div>}
          </div>
        ) : (
          <div className="flex h-full items-center  justify-center" onClick={handleEditClick}>
            <div
              className="w-[60px] pl-2  overflow-hidden text-ellipsis whitespace-wrap  h-[max-content] items-center justify-center cursor-pointer"
              dangerouslySetInnerHTML={{ __html: startv }}
            />
          </div>
        )}
      </div>
    </Tooltip>
  );
};

export default VDebut;
